import { PropsWithChildren, useCallback, useMemo } from 'react';
import {
  SelectedOrgUnit,
  SelectedOrgUnitProvider,
  SelectedOrgUnitState,
  useMyOrgUnitAssortments,
  useUserBrands,
} from '@retail/app/stores/selected-context';
import { useNavigate } from 'react-router';
import { mgPrisRoutes } from '@retail/mgpris/config';
import { useMountEffect } from '@shared/hooks';
import { useIsSuperUser } from '@retail/users/data-access';

const selectedOrgUnitAssortmentIdKey = 'selectedOrgUnitAssortmentId';

export const SelectedOrgUnitProviderImpl = (props: PropsWithChildren) => {
  const navigate = useNavigate();

  const { selectedBrand } = useUserBrands();
  const { allAssortments } = useMyOrgUnitAssortments();
  const isSuperUser = useIsSuperUser();

  // Falls back to the user's first assortment when nothing is stored, or when the stored
  // one no longer belongs to the selected brand.
  const { selectedUserAssortment, selectedOrgUnitAssortment } = useMemo(() => {
    const storedAssortmentId = Number(localStorage.getItem(selectedOrgUnitAssortmentIdKey));
    const selected =
      allAssortments.find(({ assortmentId }) => assortmentId === storedAssortmentId) ??
      allAssortments[0];

    if (!selected) {
      throw new Error(
        `User has no org unit connections for brand ${selectedBrand.name} (id: ${selectedBrand.brandId})`
      );
    }

    return {
      selectedUserAssortment: selected,
      selectedOrgUnitAssortment: SelectedOrgUnit.fromOrgUnitAssortment({
        ...selected,
        appTenant: selectedBrand.tenant,
        isSuperUser,
      }),
    };
  }, [allAssortments, isSuperUser, selectedBrand]);

  useMountEffect(() => {
    const storedOrgUnitString = localStorage.getItem(selectedOrgUnitAssortmentIdKey);
    if (!storedOrgUnitString) {
      localStorage.setItem(
        selectedOrgUnitAssortmentIdKey,
        `${selectedOrgUnitAssortment.assortmentId}`
      );
    }
  });

  const selectOrgUnitAssortment = useCallback(
    (assortmentId: number) => {
      localStorage.setItem(selectedOrgUnitAssortmentIdKey, `${assortmentId}`);
      navigate(mgPrisRoutes.root.fullRoutePath);
      window.location.reload();
    },
    [navigate]
  );

  const value: SelectedOrgUnitState = useMemo(
    () => ({
      assortmentId: selectedOrgUnitAssortment.assortmentId,
      orgUnitType: selectedOrgUnitAssortment.orgType,
      isBrandContext: selectedOrgUnitAssortment.orgType.level === 'BRAND',
      isPriceListContext: selectedOrgUnitAssortment.orgType.level === 'PRICE_LIST',
      appTenant: selectedOrgUnitAssortment.appTenant,
      selectedUserAssortment,
      selectedOrgUnitAssortment,
      selectOrgUnitAssortment,
    }),
    [selectedOrgUnitAssortment, selectedUserAssortment, selectOrgUnitAssortment]
  );

  return <SelectedOrgUnitProvider value={value}>{props.children}</SelectedOrgUnitProvider>;
};
